import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { t } from '@/i18n';
import { NO_PROJECT } from './BulkProjectDialog';
import type { InvoiceListItem, ProjectOption } from '@ip/shared-types';

interface AssignProjectDialogProps {
  /** The invoice being assigned; null keeps the dialog closed. */
  target: InvoiceListItem | null;
  /** All company projects — only ACTIVE ones are offered, but a closed
      current assignment still renders as the preselected value. */
  projects: ProjectOption[];
  updating: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whole-invoice MANUAL assignment; null clears it. */
  onSubmit: (projectId: string | null) => void;
}

/**
 * Single-invoice "Przypisz do projektu" dialog for the list row actions
 * (cost and sales). Preselects the invoice's current project and calls the
 * whole-invoice endpoint on submit — the parent owns the API call and the
 * in-place row update.
 */
export function AssignProjectDialog({
  target,
  projects,
  updating,
  onOpenChange,
  onSubmit,
}: AssignProjectDialogProps) {
  const [selection, setSelection] = useState<string>(NO_PROJECT);

  // Re-seed the selection whenever a (different) invoice is targeted.
  useEffect(() => {
    setSelection(target?.projectId ?? NO_PROJECT);
  }, [target]);

  const activeProjects = projects.filter((p) => p.status === 'active');
  const currentId = target?.projectId ?? null;

  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('invoices.assignProject.title')}</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          {t('invoices.assignProject.description', {
            invoiceNumber: target?.invoiceNumber || target?.id || '',
          })}
        </p>
        <Select value={selection} onValueChange={setSelection}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('invoices.bulk.selectProject')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_PROJECT}>
              <span className="text-muted-foreground">{t('invoices.bulk.noProject')}</span>
            </SelectItem>
            {activeProjects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                <span className="flex items-center gap-2">
                  <span
                    className="inline-block size-2 shrink-0 rounded-full border"
                    style={{ backgroundColor: project.color ?? 'transparent' }}
                    aria-hidden="true"
                  />
                  <span className="truncate">{project.name}</span>
                </span>
              </SelectItem>
            ))}
            {/* Keep a closed current assignment renderable as the value. */}
            {currentId && !activeProjects.some((p) => p.id === currentId) && (
              <SelectItem value={currentId}>
                {(target?.projectName ??
                  projects.find((p) => p.id === currentId)?.name ??
                  currentId) + ` ${t('projects.selector.closedSuffix')}`}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" disabled={updating} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={updating}
            onClick={() => onSubmit(selection === NO_PROJECT ? null : selection)}
          >
            {updating && <Loader2 className="size-4 animate-spin" />}
            {t('invoices.bulk.assignToProject')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
